import { NotificationProductEnum, UpsaleNotificationEventEnum } from './inbox.enums';

/**
 * Platform-wide notification event catalog — the registration point for every
 * product that publishes into notifications-service. Adding a product = adding
 * its entry here; the per-event email-preference grid and the feed's product
 * labels are derived from this map, never re-declared per app.
 *
 * Every event defaults to email ON; a NotificationPreference row with
 * emailEnabled=false is the only thing that turns an email off. The in-app
 * feed always receives the event regardless of the email preference.
 */
export interface NotificationCatalogEvent {
  /** Stable machine key, stored on inbox_items.event_type and notification_preferences.event_type. */
  eventType: string;
  /** Human copy for the preference row ("Send me an email when: …"). */
  label: string;
}

export interface NotificationProductCatalog {
  product: NotificationProductEnum;
  /** Product display name for feed tags and preference group headers. */
  label: string;
  events: NotificationCatalogEvent[];
}

export enum GalleryNotificationEventEnum {
  COLLECTION_DOWNLOADED = 'gallery.collection_downloaded',
  PHOTO_DOWNLOADED = 'gallery.photo_downloaded',
  VIDEO_DOWNLOADED = 'gallery.video_downloaded',
  FAVORITES_LIST_CREATED = 'gallery.favorites_list_created',
  EMAIL_REGISTERED = 'gallery.email_registered',
  PHOTO_MARKED_PRIVATE = 'gallery.photo_marked_private',
  GALLERY_EXPIRING = 'gallery.gallery_expiring',
  PHOTO_PROCESSING_FAILED = 'gallery.photo_processing_failed',
  FAVORITES_SHARED_WITH_YOU = 'gallery.favorites_shared_with_you',
  LIST_APPROVED = 'gallery.list_approved',
}

export enum StudioManagerNotificationEventEnum {
  BOOKING_CONFIRMED = 'studio.booking_confirmed',
  FORM_SUBMITTED = 'studio.form_submitted',
  DOCUMENT_SIGNED = 'studio.document_signed',
  CLIENT_REPLIED = 'studio.client_replied',
  CHANNEL_TAKEOVER = 'studio.channel_takeover',
}

export enum PhotobookNotificationEventEnum {
  BOOK_APPROVED_BY_CLIENT = 'photobook.book_approved_by_client',
  BOOKS_EXPIRING = 'photobook.books_expiring',
}

export enum FotoclassNotificationEventEnum {
  COMMENTS_DIGEST = 'fotoclass.comments_digest',
}

export const NOTIFICATION_EVENT_CATALOG: NotificationProductCatalog[] = [
  {
    product: NotificationProductEnum.GALLERY,
    label: 'Клиентские галереи',
    events: [
      { eventType: GalleryNotificationEventEnum.COLLECTION_DOWNLOADED, label: 'Кто-то скачал галерею целиком' },
      { eventType: GalleryNotificationEventEnum.PHOTO_DOWNLOADED, label: 'Кто-то скачал отдельное фото' },
      { eventType: GalleryNotificationEventEnum.VIDEO_DOWNLOADED, label: 'Кто-то скачал видео' },
      { eventType: GalleryNotificationEventEnum.FAVORITES_LIST_CREATED, label: 'Создан новый список избранного' },
      { eventType: GalleryNotificationEventEnum.EMAIL_REGISTERED, label: 'Новая email-регистрация в галерее' },
      { eventType: GalleryNotificationEventEnum.PHOTO_MARKED_PRIVATE, label: 'Клиент сделал фото приватным' },
      { eventType: GalleryNotificationEventEnum.GALLERY_EXPIRING, label: 'Галерея удалится через 7 дней' },
      { eventType: GalleryNotificationEventEnum.PHOTO_PROCESSING_FAILED, label: 'Фото не удалось обработать' },
      { eventType: GalleryNotificationEventEnum.FAVORITES_SHARED_WITH_YOU, label: 'С вами поделились списком избранного' },
      { eventType: GalleryNotificationEventEnum.LIST_APPROVED, label: 'Клиент утвердил список' },
    ],
  },
  {
    product: NotificationProductEnum.STUDIO_MANAGER,
    label: 'Маркетинг',
    events: [
      { eventType: StudioManagerNotificationEventEnum.BOOKING_CONFIRMED, label: 'Съёмка подтверждена' },
      { eventType: StudioManagerNotificationEventEnum.FORM_SUBMITTED, label: 'Новая заявка из формы' },
      { eventType: StudioManagerNotificationEventEnum.DOCUMENT_SIGNED, label: 'Клиент подписал документ' },
      { eventType: StudioManagerNotificationEventEnum.CLIENT_REPLIED, label: 'Клиент ответил в переписке' },
      { eventType: StudioManagerNotificationEventEnum.CHANNEL_TAKEOVER, label: 'Клиент переключил Telegram-переписку' },
    ],
  },
  {
    product: NotificationProductEnum.PHOTOBOOK,
    label: 'Фотокниги',
    events: [
      { eventType: PhotobookNotificationEventEnum.BOOK_APPROVED_BY_CLIENT, label: 'Клиент утвердил книгу' },
      { eventType: PhotobookNotificationEventEnum.BOOKS_EXPIRING, label: 'Книга удалится через 7 дней' },
    ],
  },
  {
    product: NotificationProductEnum.FOTOCLASS,
    label: 'Выпускные альбомы',
    events: [
      { eventType: FotoclassNotificationEventEnum.COMMENTS_DIGEST, label: 'Новые комментарии в альбомах' },
      { eventType: UpsaleNotificationEventEnum.UPSALE_PURCHASES, label: 'Новые покупки в проектах' },
      { eventType: UpsaleNotificationEventEnum.UPSALE_ORGANIZER_PAID, label: 'Организатор отметил оплату' },
    ],
  },
];

export const NOTIFICATION_PRODUCT_LABELS: Record<string, string> = Object.fromEntries(
  NOTIFICATION_EVENT_CATALOG.map(c => [c.product, c.label]),
);

export function findCatalogEvent(product: string, eventType: string): NotificationCatalogEvent | undefined {
  return NOTIFICATION_EVENT_CATALOG.find(c => c.product === product)?.events.find(e => e.eventType === eventType);
}
